import {Component, computed, effect, inject, OnDestroy, untracked, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FilterBarComponent} from '../shared/filter-bar/filter-bar.component';
import {MatCardModule} from '@angular/material/card';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatPaginator, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatIcon} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {AuthService} from '../services/auth.service';
import {ApiService} from '../services/api.service';
import {SettingsService, utcOffsetValue} from '../services/settings.service';
import {CSV_DOWNLOAD_HINT, DataService} from '../services/data.service';
import {UnProcessedVin} from '../models/unprocessedVin/UnProcessedVin.model';
import {toObservable} from '@angular/core/rxjs-interop';
import { skip } from 'rxjs/operators';
import { DataFilterService } from '../services/data-filter.service';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { pieSliceLabels, whiteCanvasBackground } from '../shared/chart-plugins';
import { ChartConfiguration, ChartData } from 'chart.js';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { BaseChartDirective } from 'ng2-charts';
import { latestLoad } from '../shared/latest-load';

type DateRange = { start: Date; end: Date };

/** One table request: the range, and the cursor for the page wanted. */
interface TableLoad {
  dateRange: DateRange;
  cursor: string | null;
}

const INVENTORY_COLORS = {
  unprocessed: '#646464',
  processed: '#949494'
} as const;

@Component({
  selector: 'app-unprocessed-vins',
  imports: [
    FilterBarComponent,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule,
    MatIcon,
    MatButtonModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    /* BaseChartDirective */
  ],
  templateUrl: './unprocessed-vins.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './unprocessed-vins.component.scss'
})
export class UnprocessedVinsComponent implements OnDestroy {
  readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly settings = inject(SettingsService);
  private readonly filters = inject(DataFilterService);
  readonly data = inject(DataService);
  tableLoading: boolean = false;

  readonly csvDownloadHint = CSV_DOWNLOAD_HINT;

  readonly displayedColumns = [
    'dateInInventory',
    'status',
    'vinCode',
    'decodedMake',
    'decodedModel',
    'decodedYear',
    'processorSite',
    'username',
    'controllerName',
    'controllerId',
    'classification'
  ];

  readonly tableHeaderTooltips = {
    dateInInventory: "Date the vehicle was logged into inventory",
    status: "Processing Status",
    vinCode: "17-digit vehicle ID",
    decodedMake: "Vehicle manufacturer",
    decodedModel: "Vehicle model",
    decodedYear: "Vehicle manufacturing year",
    processorSite: "Name of the site processing the vehicle",
    username: "",
    controllerName: "Name of the company who owns the vehicle",
    controllerId: "ID of the company who owns the vehicle",
    classification: "Vehicle inventory processing category"
  }

  dataSource = new MatTableDataSource<UnProcessedVin>([]);

  @ViewChild(MatPaginator) private paginator!: MatPaginator;

  // --- Unprocessed inventory pie ---

  unprocessedVsProcessedCountsLoading: boolean = false;

  /** Table loads — only the latest counts; see latestLoad. */
  private readonly loadTable = latestLoad<TableLoad>(
    ({ dateRange, cursor }) => this.api.getUnProcessedVinsPaginated(
      toIsoDate(dateRange.start),
      toIsoDate(dateRange.end),
      utcOffsetValue(this.settings.timezone(), dateRange.start),
      cursor,
      this.data.scanUnprocessedVinTablePagination().pageSize,
      true // the report reflects the user's selection
    ),
    active => this.tableLoading = active
  );

  /**
   * Counts loads. They reload on every timezone change, and a closed timezone
   * select changes value on each arrow-key press — so several can be in flight
   * at once, and only the latest may land.
   */
  private readonly loadUnprocessedVsProcessedCounts = latestLoad<string>(
    timezone => this.api.getUnprocessedVsProcessedCounts(utcOffsetValue(timezone)),
    active => this.unprocessedVsProcessedCountsLoading = active
  );

  readonly pieChartType = 'pie' as const;

  /** Stable reference — see chartPlugins. */
  readonly inventoryChartPlugins = [whiteCanvasBackground, pieSliceLabels];

  /**
   * Unprocessed vs processed inventory with the derived total and shares.
   *
   * The processed share is the remainder rather than its own rounding, so the
   * two printed percentages always sum to 100 — rounding both independently
   * can produce 101 (12.5% and 87.5%, say). The wedges are drawn from the raw
   * counts, so the display rounding never distorts the geometry.
   */
  readonly inventory = computed(() => {
    const { unprocessedCount, processedCount } = this.data.unprocessedVsProcessedCounts();
    const total = unprocessedCount + processedCount;
    const unprocessedShare = total ? Math.round((unprocessedCount / total) * 100): 0;
    return {
      unprocessed: unprocessedCount,
      processed: processedCount,
      total,
      unprocessedShare,
      processedShare: total ? 100 - unprocessedShare : 0
    };
  });

  readonly inventoryTotalLabel = computed(() => {
    const total = this.inventory().total;
    const formatted = new Intl.NumberFormat(this.settings.locale()).format(total);
    /* return `${formatted} ${total === 1 ? 'vehicle' : 'vehicles'}`; */
    return `${formatted}`;
  });

  readonly inventoryChartData = computed<ChartData<'pie'>>(() => {
    const inventory = this.inventory();
    return {
      labels: ['Unprocessed', 'Processed'],
      datasets: [{
        data: [inventory.unprocessed, inventory.processed],
        backgroundColor: [INVENTORY_COLORS.unprocessed, INVENTORY_COLORS.processed],
        borderColor: '#000',
        borderWidth: 2
      }]
    };
  });

  /**
   * A computed rather than a field so a locale change reaches the slice
   * labels' number formatting; the reference only changes when the locale does.
   */
  readonly inventoryChartOptions = computed<ChartConfiguration<'pie'>['options']>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    locale: this.settings.locale(),
    layout: { padding: 8 },
    plugins: {
      // Drawn as swatch rows beside the chart so the descriptions can wrap.
      legend: { display: false }
    }
  }));

  /** Legend rows, wording from the card's wireframe. Static — no signal reads. */
  readonly inventoryLegend = computed(() => ([
    {
      color: INVENTORY_COLORS.unprocessed,
      fontColor: '#fff',
      label: 'unprocessed',
      text: 'vehicles not yet scanned with the Privacy4Cars app',
      count:  new Intl.NumberFormat(this.settings.locale()).format(this.inventory().unprocessed),
      share: this.inventory().unprocessedShare
    },
    /* {
      color: INVENTORY_COLORS.unprocessed,
      fontColor: '#fff',
      label: 'Unprocessed',
      text: 'vehicles not yet scanned with the Privacy4Cars app',
      count:  new Intl.NumberFormat(this.settings.locale()).format(this.inventory().unprocessed),
      share: this.inventory().unprocessedShare
    }, */
    /* {
      color: INVENTORY_COLORS.processed,
      fontColor: '#fff',
      label: 'Processed',
      text: 'see "Activity by Date" for status of each vehicle',
      count: new Intl.NumberFormat(this.settings.locale()).format(this.inventory().processed),
      share: this.inventory().processedShare
    } */
  ]));

  constructor() {
    toObservable(this.data.newScansVin).subscribe(newScansVin => {
      this.dataSource.data = newScansVin;
    })

    toObservable(this.data.dateRange).subscribe(dateRange => this.reload(dateRange));

    // Filters are request inputs, so a change has to re-request. skip(1): the
    // initial load is driven by dateRange, and replaying the current filters on
    // mount would double it. Later emissions are always real changes — the
    // applied signal's equality suppresses re-applying an unchanged selection.
    toObservable(this.filters.applied).pipe(skip(1))
      .subscribe(() => this.reload(this.data.dateRange()));

    toObservable(this.settings.timezone).subscribe(timezone => {

      if (this.settings.defaultTimezoneInitialized == true) {
        this.reload(this.data.dateRange());
      } else {
        this.settings.defaultTimezoneInitialized = true;
      }

    })

    effect(() => {

      const timezone = this.settings.timezone();

      untracked(() => this.loadUnprocessedVsProcessedCounts(timezone));

    });

    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.settings.defaultTimezoneInitialized = false;
  }

  paginatorEventHandler(ev: PageEvent) {
    if (ev.previousPageIndex && ev.pageIndex < ev.previousPageIndex) {
      this.loadTableData(this.data.dateRange(), false, false, true);
    } else {
      this.loadTableData(this.data.dateRange(), false, true, false);
    }
  }

  /** Back to the first page, then reload the table. */
  private reload(dateRange: DateRange | null): void {
    // Left exactly as before: the three report paginators currently share one
    // object, so this reset must not change on its own.
    this.data.setDefaultScanTablePagination();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadTableData(dateRange, true, false, false);
  }

  loadTableData(dateRange: DateRange | null, refresh: boolean, next: boolean, previous: boolean) {

    if (dateRange == null) {
      return;
    }

    const pagination = this.data.scanUnprocessedVinTablePagination();

    this.dataSource.data = [];

    this.loadTable({
      dateRange,
      cursor: refresh ? null : next ? pagination.cursorNext : pagination.cursorPrev
    });

  }

  formatDateTime(iso: string): string {
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'medium',
      timeZone: this.settings.timezone()
    }).format(new Date(iso));
  }

  exportUnprocessedVinReportCSV() {

    const dateRange = this.data.dateRange();

    if (dateRange != null) {
      this.api.exportUnprocessedVinReportCSV(
        toIsoDate(dateRange.start),
        toIsoDate(dateRange.end),
        utcOffsetValue(this.settings.timezone(), dateRange.start),
        true // the export matches the table on screen
      );
    }

  }

}
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
